<template>
  <Dialog
    :open="open"
    :class="sizeClass"
    @update:open="onOpenChange"
  >
    <DialogContent>
      <!-- Header -->
      <DialogHeader v-if="title" class="bg-muted/50 border-b">
        <DialogTitle>
          {{ title }}
        </DialogTitle>
      </DialogHeader>

      <!-- Body -->
      <DialogBody>
        <div class="space-y-4">
          <!-- Description -->
          <p v-if="description" class="text-sm text-muted-foreground">
            {{ description }}
          </p>

          <!-- JFormField가 있는 경우 -->
          <div v-if="showFormField">
            <JFormField
              :id="inputId"
              v-model="inputValue"
              :label="formFieldLabel"
              :error-msg="formFieldError"
              :required="formFieldRequired"
              :type="formFieldType"
              :input-type="formFieldInputType"
              :placeholder="formFieldInputPlaceholder"
              :disabled="disabled"
            />
          </div>

          <!-- 커스텀 컨텐츠 슬롯 -->
          <slot name="body" />
        </div>
      </DialogBody>

      <!-- Footer -->
      <DialogFooter>
        <!-- 확인/취소 버튼 -->
        <template v-if="buttonType === 'OkCancel'">
          <JButton
            variant="outline"
            size="sm"
            @click="handleCancel"
          >
            {{ cancelText }}
          </JButton>
          <JButton
            :variant="confirmVariant"
            :disabled="confirmDisabled"
            size="sm"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </JButton>
        </template>
        
        <!-- 확인 버튼만 -->
        <template v-else-if="buttonType === 'Ok'">
          <JButton
            :variant="confirmVariant"
            :disabled="confirmDisabled"
            size="sm"
            class="w-full"
            @click="handleConfirm"
          >
            {{ confirmText }}
          </JButton>
        </template>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn'
import { JButton } from '@/components/atoms'
import { JFormField } from '@/components/molecules'

export interface JModalProps {
  // 모달 표시 여부
  open: boolean
  // 헤더
  title?: string
  description?: string
  // 모달 사이즈
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
  // 폼 필드 (JFormField)
  showFormField?: boolean
  formFieldLabel?: string
  formFieldError?: string
  formFieldRequired?: boolean
  // 입력 필드 설정
  formFieldInputType?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url'
  formFieldInputPlaceholder?: string
  formFieldValue?: string
  formFieldType?: 'input' | 'textarea' | 'checkbox' | 'switch' | 'combo' | 'radio' | 'searchCombo' | 'datepicker'
  // 버튼 설정
  buttonType?: 'Ok' | 'OkCancel'
  confirmText?: string
  cancelText?: string
  confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  confirmDisabled?: boolean
  // 기타
  disabled?: boolean
}

const props = withDefaults(defineProps<JModalProps>(), {
  open: false,
  size: 'md',
  showFormField: false,
  formFieldInputType: 'text',
  formFieldType: 'input',
  buttonType: 'OkCancel',
  confirmText: '확인',
  cancelText: '취소',
  confirmVariant: 'default',
  confirmDisabled: false,
  disabled: false,
})

const emit = defineEmits<{
  'update:open': [value: boolean]
  'confirm': [value?: string]
  'cancel': []
}>()

// 입력값 관리
const inputValue = ref(props.formFieldValue || '')

// 입력 필드 ID 생성
const inputId = computed(() => `modal-input-${Math.random().toString(36).substr(2, 9)}`)

// 사이즈 클래스 계산
const sizeClass = computed(() => {
  // Dialog.vue에 기본 max-w-lg가 하드코딩되어 있어 !max-w-*로 오버라이드
  const sizeMap: Record<NonNullable<JModalProps['size']>, string> = {
    'sm': '!max-w-sm',
    'md': '!max-w-md',
    'lg': '!max-w-2xl',
    'xl': '!max-w-4xl',
    '2xl': '!max-w-6xl',
    'full': '!max-w-[95vw]'
  }
  return sizeMap[props.size]
})

// props.formFieldValue 변경 감지
watch(() => props.formFieldValue, (newValue) => {
  if (newValue !== undefined) {
    inputValue.value = newValue
  }
})

// 모달 열기/닫기 핸들러
const onOpenChange = (value: boolean) => {
  emit('update:open', value)
}

// 확인 버튼 핸들러
const handleConfirm = () => {
  const value = props.showFormField ? inputValue.value : undefined
  emit('confirm', value)
}

// 취소 버튼 핸들러
const handleCancel = () => {
  emit('cancel')
  // 입력값 초기화
  inputValue.value = props.formFieldValue || ''
}
</script>
